/**
 * /partner – KPI-Dashboard: Klicks, Conversions, Provisionen.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PartnerLayout from "@/components/partner/PartnerLayout";
import PartnerHowItWorks from "@/components/partner/PartnerHowItWorks";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyAffiliate } from "@/hooks/useMyAffiliate";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, MousePointerClick, Users, Hourglass, CheckCircle2, Banknote, Percent } from "lucide-react";

const formatEur = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);

interface Kpi {
  clicks: number;
  conversions: number;
  pending: number;
  approved: number;
  paid: number;
}

const PartnerDashboard = () => {
  const { data: user } = useCurrentUser();
  const { data: affiliate } = useMyAffiliate(user?.id);

  const { data: kpi, isLoading } = useQuery({
    queryKey: ["partner-kpi", affiliate?.id],
    enabled: !!affiliate?.id,
    queryFn: async (): Promise<Kpi> => {
      const aid = affiliate!.id;
      const [clicksRes, refRes, commRes] = await Promise.all([
        supabase.from("affiliate_clicks").select("id", { count: "exact", head: true }).eq("affiliate_id", aid),
        supabase.from("affiliate_referrals").select("id", { count: "exact", head: true }).eq("affiliate_id", aid),
        supabase.from("affiliate_commissions").select("status, commission_amount").eq("affiliate_id", aid),
      ]);
      const rows = (commRes.data ?? []) as Array<{ status: string; commission_amount: number | null }>;
      const sumBy = (s: string) =>
        rows.filter((r) => r.status === s).reduce((acc, r) => acc + Number(r.commission_amount ?? 0), 0);
      return {
        clicks: clicksRes.count ?? 0,
        conversions: refRes.count ?? 0,
        pending: sumBy("pending"),
        approved: sumBy("approved"),
        paid: sumBy("paid"),
      };
    },
    staleTime: 30_000,
  });

  // Standard-Provisionssatz aus den Affiliate-Einstellungen laden (RLS: alle authentifizierten User dürfen lesen)
  const { data: settings } = useQuery({
    queryKey: ["affiliate-settings-default"],
    queryFn: async () => {
      const { data } = await supabase
        .from("affiliate_settings")
        .select("default_commission_rate")
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 5 * 60_000,
  });

  if (isLoading || !kpi) {
    return (
      <PartnerLayout title="Dashboard">
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </PartnerLayout>
    );
  }

  const conversionRate = kpi.clicks > 0 ? (kpi.conversions / kpi.clicks) * 100 : 0;
  const overrideRate = affiliate?.commission_rate_override;
  const defaultRate = Number(settings?.default_commission_rate ?? 0);
  const effectiveRate = overrideRate !== null && overrideRate !== undefined ? Number(overrideRate) : defaultRate;
  const hasOverride = overrideRate !== null && overrideRate !== undefined;

  const cards = [
    {
      label: "Dein Provisionssatz",
      value: `${effectiveRate.toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`,
      icon: Percent,
      hint: hasOverride ? "Individuell für dich vereinbart." : "Standardsatz für alle Partner.",
    },
    { label: "Klicks", value: kpi.clicks.toString(), icon: MousePointerClick },
    { label: "Conversions", value: `${kpi.conversions} (${conversionRate.toFixed(1)}%)`, icon: Users },
    { label: "Offen (Widerruf)", value: formatEur(kpi.pending), icon: Hourglass, hint: "Wird nach 30 Tagen freigegeben." },
    { label: "Auszahlbar", value: formatEur(kpi.approved), icon: CheckCircle2 },
    { label: "Bereits ausgezahlt", value: formatEur(kpi.paid), icon: Banknote },
  ];

  return (
    <PartnerLayout title="Dashboard">
      <PartnerHowItWorks />
      <h2 className="mb-3 text-lg font-semibold">Deine Statistiken</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <Card key={c.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{c.label}</CardTitle>
              <c.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{c.value}</div>
              {c.hint && <p className="mt-1 text-xs text-muted-foreground">{c.hint}</p>}
            </CardContent>
          </Card>
        ))}
      </div>
    </PartnerLayout>
  );
};

export default PartnerDashboard;
